import React, { useState } from 'react';
import { sanitizeHtml } from '../utils/sanitize';
import {
  FaCheckSquare,
  FaRegSquare,
  FaClock,
  FaChevronDown,
  FaChevronUp,
  FaMapMarkerAlt,
  FaCompass,
  FaUtensils,
  FaLandmark,
  FaTree,
  FaHiking,
  FaUmbrellaBeach,
  FaChurch,
  FaHotel,
  FaShoppingBag,
  FaTheaterMasks,
  FaWater,
  FaGlassMartini,
  FaUniversity,
  FaMonument,
  FaStar,
  FaBed,
  FaLock,
  FaLockOpen
} from 'react-icons/fa';
import ReviewLinks from './ReviewLinks';
import BookingOptions from './BookingOptions';
import { usePlaceImage, isTicketableAttraction } from '@travel-planner/shared';

// Category detection patterns with icons.
// `bg` must be a full literal class (including opacity modifier) so Tailwind's
// content scanner detects it — concatenating opacity at runtime would silently
// drop the class from the CSS bundle.
const CATEGORY_CONFIG = [
  { pattern: /restaurant|cafe|bistro|kitchen|grill|diner|eatery|food|bakery|pizzeria|taqueria|sushi|burger|bbq|steakhouse|dining|breakfast|lunch|dinner|brunch|meal/i, icon: FaUtensils, color: 'text-orange-500', bg: 'bg-orange-50/70' },
  { pattern: /museum|gallery|art\s|exhibit/i, icon: FaLandmark, color: 'text-purple-500', bg: 'bg-purple-50/70' },
  { pattern: /park|garden|botanical|arboretum|nature/i, icon: FaTree, color: 'text-green-500', bg: 'bg-green-50/70' },
  { pattern: /trail|hike|hiking|mountain|canyon|peak|summit/i, icon: FaHiking, color: 'text-amber-600', bg: 'bg-amber-50/70' },
  { pattern: /beach|coast|ocean|sea|shore|surf/i, icon: FaUmbrellaBeach, color: 'text-cyan-500', bg: 'bg-cyan-50/70' },
  { pattern: /temple|church|cathedral|mosque|shrine|chapel|basilica/i, icon: FaChurch, color: 'text-indigo-500', bg: 'bg-indigo-50/70' },
  { pattern: /hotel|resort|spa|lodge|inn/i, icon: FaHotel, color: 'text-pink-500', bg: 'bg-pink-50/70' },
  { pattern: /market|shop|mall|bazaar|store|boutique/i, icon: FaShoppingBag, color: 'text-rose-500', bg: 'bg-rose-50/70' },
  { pattern: /theater|theatre|cinema|opera|concert|stadium|arena/i, icon: FaTheaterMasks, color: 'text-red-500', bg: 'bg-red-50/70' },
  { pattern: /waterfall|lake|river|spring|pond|dam|falls/i, icon: FaWater, color: 'text-blue-500', bg: 'bg-blue-50/70' },
  { pattern: /bar|pub|brewery|winery|vineyard|tavern|lounge/i, icon: FaGlassMartini, color: 'text-violet-500', bg: 'bg-violet-50/70' },
  { pattern: /university|college|school|campus|library/i, icon: FaUniversity, color: 'text-slate-500', bg: 'bg-slate-50/70' },
  { pattern: /bridge|monument|memorial|statue|tower|castle|palace|fort/i, icon: FaMonument, color: 'text-stone-500', bg: 'bg-stone-50/70' },
];

// Detect category from location name
const detectCategory = (name) => {
  for (const config of CATEGORY_CONFIG) {
    if (config.pattern.test(name)) {
      return config;
    }
  }
  // Default
  return { icon: FaMapMarkerAlt, color: 'text-sky-500', bg: 'bg-sky-50/70' };
};

// Format time for display (convert 24h to 12h format)
const formatTime = (time) => {
  if (!time) return '';
  const [hours, minutes] = time.split(':');
  const h = parseInt(hours, 10);
  const ampm = h >= 12 ? 'PM' : 'AM';
  const hour12 = h % 12 || 12;
  return `${hour12}:${minutes} ${ampm}`;
};

// Pattern to detect accommodations
const ACCOMMODATION_PATTERN = /\bhotel\b|\bresort\b|\bmotel\b|\bhostel\b|\bairbnb\b|\bb&b\b|\bbed\s*&?\s*breakfast\b|\bguesthouse\b|\bguest\s*house\b|\bglamping\b|\bvilla\b|\bchalet\b|\bresidence\b|\bresidency\b|\bpenthouse\b|\baccomodation\b|\baccommodation\b|\briad\b|\bryokan\b|\bpension\b|\bposada\b|\bpousada\b|\bhacienda\b|\bmanor\b|\bchateau\b|\bcastle\s*hotel\b/i;

const AttractionCard = ({
  locationInfo,
  formattedHtml,
  description,
  destination,
  isVisited,
  isLocked,
  customTime,
  isOwner,
  showAttractionControls,
  showReviewLinks,
  onAttractionStatusUpdate,
  dayNumber,
  showExploreButton,
  onExplore,
  nextLoc,
  bookingLink,
  starRating,
  checkInDate,
  checkOutDate,
  // Supplied by the caller, which has already applied the parser's promotion
  // rules. The name regex below is only a fallback for callers that don't.
  isAccommodation: isAccommodationProp,
  bookingSurface,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isEditingTime, setIsEditingTime] = useState(false);

  const hasCoordinates = locationInfo.lat && locationInfo.lng;
  const category = detectCategory(locationInfo.name);
  const CategoryIcon = category.icon;
  const isAccommodation =
    isAccommodationProp !== undefined
      ? isAccommodationProp
      : ACCOMMODATION_PATTERN.test(locationInfo.name);
  const isTicketable = isTicketableAttraction(locationInfo.name, description, isAccommodation);
  const placeImageUrl = usePlaceImage(locationInfo.name, locationInfo.lat, locationInfo.lng);

  // Generate Google Maps directions URL
  const getDirectionsUrl = () => {
    if (hasCoordinates) {
      return `https://www.google.com/maps/dir/?api=1&destination=${locationInfo.lat},${locationInfo.lng}`;
    }
    const searchQuery = destination ? `${locationInfo.name} ${destination}` : locationInfo.name;
    return `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(searchQuery)}`;
  };

  return (
    <div className={`
      rounded-xl border bg-white shadow-sm hover:shadow-md transition-all duration-200 mb-3
      ${isLocked
        ? 'border-l-4 border-l-amber-500 border-t-neutral-200 border-r-neutral-200 border-b-neutral-200'
        : isVisited
          ? 'border-l-4 border-l-green-500 border-t-neutral-200 border-r-neutral-200 border-b-neutral-200'
          : 'border-neutral-200'
      }
    `}>
      {/* Card Header with gradient accent */}
      <div className={`px-4 py-3 ${isLocked ? 'bg-amber-50/50' : isVisited ? 'bg-green-50/50' : category.bg}`}>
        <div className="flex items-center gap-3">
          {/* Place Image or No Image placeholder */}
          {placeImageUrl ? (
            <img
              src={placeImageUrl}
              alt={locationInfo.name}
              loading="lazy"
              className="flex-shrink-0 w-[72px] h-[72px] rounded-lg object-cover"
            />
          ) : (
            <div className="flex-shrink-0 w-[72px] h-[72px] rounded-lg bg-neutral-100 flex items-center justify-center">
              <div className="text-center">
                <CategoryIcon className="text-neutral-300 text-lg mx-auto" />
                <span className="text-[9px] text-neutral-400 font-medium">No Image</span>
              </div>
            </div>
          )}

          {/* Visited checkbox + Name */}
          <div className="flex-1 min-w-0 flex items-center gap-2">
            {showAttractionControls && onAttractionStatusUpdate && hasCoordinates && (
              <button
                onClick={() => {
                  if (!isVisited || isOwner) {
                    onAttractionStatusUpdate(
                      dayNumber,
                      locationInfo.name,
                      { lat: locationInfo.lat, lng: locationInfo.lng },
                      { visited: !isVisited }
                    );
                  }
                }}
                disabled={isVisited && !isOwner}
                className={`flex-shrink-0 transition-colors ${
                  isVisited
                    ? isOwner
                      ? 'text-green-600 hover:text-green-700 cursor-pointer'
                      : 'text-green-500 cursor-not-allowed'
                    : 'text-neutral-400 hover:text-neutral-600 cursor-pointer'
                }`}
                title={
                  isVisited
                    ? isOwner
                      ? 'Click to unmark as visited'
                      : 'Visited (only owner can unmark)'
                    : 'Mark as visited'
                }
              >
                {isVisited ? <FaCheckSquare className="text-xl" /> : <FaRegSquare className="text-xl" />}
              </button>
            )}

            {/* Lock button - only visible to owner */}
            {showAttractionControls && onAttractionStatusUpdate && hasCoordinates && isOwner && (
              <button
                onClick={() => {
                  onAttractionStatusUpdate(
                    dayNumber,
                    locationInfo.name,
                    { lat: locationInfo.lat, lng: locationInfo.lng },
                    { locked: !isLocked }
                  );
                }}
                className={`flex-shrink-0 transition-colors ${
                  isLocked
                    ? 'text-amber-600 hover:text-amber-700'
                    : 'text-neutral-400 hover:text-neutral-600'
                }`}
                title={isLocked ? 'Click to unlock (allow optimization)' : 'Lock this stop (prevent optimization)'}
              >
                {isLocked ? <FaLock className="text-lg" /> : <FaLockOpen className="text-lg" />}
              </button>
            )}

            <h4
              className={`font-semibold text-neutral-800 truncate ${isVisited ? 'line-through opacity-70' : ''}`}
              dangerouslySetInnerHTML={{ __html: sanitizeHtml(formattedHtml) }}
            />
            {/* Star Rating */}
            {starRating && (
              <span className="flex items-center gap-0.5 text-amber-500 ml-2 flex-shrink-0">
                {[...Array(Math.min(starRating, 5))].map((_, i) => (
                  <FaStar key={i} size={12} />
                ))}
              </span>
            )}
          </div>

          {/* Time Badge */}
          {showAttractionControls && onAttractionStatusUpdate && hasCoordinates && (
            <div className="flex-shrink-0">
              {isEditingTime ? (
                <input
                  type="time"
                  value={customTime}
                  onChange={(e) => {
                    if (!isVisited || isOwner) {
                      onAttractionStatusUpdate(
                        dayNumber,
                        locationInfo.name,
                        { lat: locationInfo.lat, lng: locationInfo.lng },
                        { custom_time: e.target.value }
                      );
                    }
                  }}
                  onBlur={() => setIsEditingTime(false)}
                  autoFocus
                  disabled={isVisited && !isOwner}
                  className="px-2 py-1 text-sm border border-sky-300 rounded-lg focus:ring-2 focus:ring-sky-500 focus:border-sky-500 w-[110px] bg-white"
                />
              ) : (
                <button
                  onClick={() => {
                    if (!isVisited || isOwner) {
                      setIsEditingTime(true);
                    }
                  }}
                  disabled={isVisited && !isOwner}
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                    customTime
                      ? 'bg-sky-100 text-sky-700 hover:bg-sky-200'
                      : isVisited && !isOwner
                        ? 'bg-neutral-100 text-neutral-400 cursor-not-allowed'
                        : 'bg-neutral-100 text-neutral-500 hover:bg-neutral-200 cursor-pointer'
                  }`}
                  title={
                    isVisited && !isOwner
                      ? 'Only owner can modify time'
                      : customTime
                        ? 'Click to change time'
                        : 'Click to set time'
                  }
                >
                  <FaClock size={12} />
                  {customTime ? formatTime(customTime) : 'Set time'}
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Card Body */}
      <div className="px-4 py-3">
        {/* Booking options for accommodations mentioned as stays in the itinerary */}
        {isAccommodation && (
          <div className="mb-3">
            <BookingOptions
              type="hotel"
              hotelName={locationInfo.name}
              city={destination}
              checkIn={checkInDate}
              checkOut={checkOutDate}
              surface={bookingSurface ? `${bookingSurface}_day${dayNumber || 1}` : undefined}
            />
          </div>
        )}

        {/* Description (if available) - truncated with expand */}
        {description && (
          <div className="mb-3">
            <p className={`text-sm text-neutral-600 leading-relaxed ${!isExpanded ? 'line-clamp-3' : ''}`}>
              {description}
            </p>
            {description.length > 120 && (
              <button
                onClick={() => setIsExpanded(!isExpanded)}
                className="text-xs text-sky-600 hover:text-sky-700 mt-1.5 flex items-center gap-1 font-medium"
              >
                {isExpanded ? (
                  <>Show less <FaChevronUp size={10} /></>
                ) : (
                  <>Show more <FaChevronDown size={10} /></>
                )}
              </button>
            )}
          </div>
        )}

        {/* Action buttons row */}
        <div className={`flex items-center gap-2 flex-wrap ${description || (isAccommodation && bookingLink) ? 'pt-3 border-t border-neutral-100' : ''}`}>
          {/* More Info button */}
          <a
            href={`https://www.google.com/search?q=${encodeURIComponent(locationInfo.name + ' ' + destination)}`}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-purple-700 bg-purple-50 hover:bg-purple-100 rounded-lg transition-colors"
          >
            More Info
          </a>

          {/* Directions button */}
          <a
            href={getDirectionsUrl()}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-blue-700 bg-blue-50 hover:bg-blue-100 rounded-lg transition-colors"
          >
            <FaMapMarkerAlt size={12} />
            Directions
          </a>

          {/* Reviews button */}
          {showReviewLinks && (
            <ReviewLinks
              name={locationInfo.name}
              location={destination}
              coordinates={hasCoordinates ? { lat: locationInfo.lat, lng: locationInfo.lng } : null}
              compact={true}
            />
          )}

          {/* Tickets — only where a ticket plausibly exists, so the row doesn't
              grow a dead link on every park bench and side street. */}
          {isTicketable && (
            <BookingOptions
              type="attraction"
              attractionName={locationInfo.name}
              city={destination}
              checkIn={checkInDate}
              compact={true}
              surface={bookingSurface ? `${bookingSurface}_day${dayNumber || 1}` : undefined}
            />
          )}

        </div>

        {/* Explore stops along the way - shown as a separate row for prominence */}
        {showExploreButton && onExplore && nextLoc && (
          <div className="mt-3 pt-3 border-t border-neutral-100">
            <button
              onClick={() => onExplore(
                locationInfo.name,
                nextLoc.name,
                { lat: locationInfo.lat, lng: locationInfo.lng },
                { lat: nextLoc.lat, lng: nextLoc.lng }
              )}
              className="w-full flex items-center justify-center gap-2 py-2.5 text-xs font-medium text-emerald-700 bg-gradient-to-r from-emerald-50 to-teal-50 hover:from-emerald-100 hover:to-teal-100 rounded-lg transition-colors border border-emerald-200"
            >
              <FaCompass className="text-emerald-500" />
              Explore stops along the way to {nextLoc.name.length > 25 ? nextLoc.name.substring(0, 25) + '...' : nextLoc.name}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default AttractionCard;
